<template>
  <div class="contacts-filter-container">
    <slot name="bar"/>

    <template v-if="layout === 'block'">
      <div class="mt-3">
        <div class="d-flex justify-content-start align-items-center">
          <h4 v-if="loading">{{ $t('general.loading') }}</h4>
          <h4 v-else class="d-none d-sm-block">{{ $tc('manage.total-results', total) }}</h4>
          <div class="flex-fill"/>
          <sort v-if="orderOptions" i18n-prefix="contacts.orderBy" :options="orderOptions" :value="order" @input="v => $emit('orderUpdated', v)"/>
          <toggle-filters class="ml-3" v-if="$slots['under-filters']" v-model="showHorizontalWhere"/>
        </div>
      </div>

      <b-card class="horizontal-where" body-class="p-3" v-if="layout === 'block' && showHorizontalWhere">
        <slot name="where-options">
          <horizontal-options orientation="horizontal" :value="where" @input="v => $emit('whereUpdated', v)"/>
        </slot>
      </b-card>

      <loading-spinner class="my-5 text-center" v-if="loading"/>

      <template v-if="total">
        <slot name="results" :edges="results.edges">
          <b-row class="card-deck mt-3 px-3 px-md-0">
            <template v-for="({ entry, relevance }, i) in results.edges">
              <contacts-filter-card class="mb-3" v-if="entry && entry.id" :key="entry.id" :contact="entry" :relevance="relevance">
                <template v-slot:actions="opts">
                  <slot name="contact-card-actions" v-bind="opts"/>
                </template>
              </contacts-filter-card>
              <div class="w-100 mt-4" v-if="cols[i].divider === true"></div>
            </template>
          </b-row>
        </slot>
        <div v-if="page && totalPages" class="filter-pagination">
          <p>{{ $t('manage.page-n-of-t', { page, totalPages }) }}</p>
          <b-pagination :value="page" @input="v => $emit('pageUpdated', v)" :per-page="limit" :total-rows="totalPages * limit"/>
        </div>
      </template>
      <slot v-else-if="results" name="no-results">
        <p class="my-3 text-center">{{ $tc('manage.total-results', 0) }}</p>
      </slot>
    </template>
    <template v-else-if="layout === 'page'">
      <b-row class="mt-3">
        <b-col md="8" order="2" order-md="1">
          <loading-spinner class="my-5 text-center" v-if="loading"/>
          <template v-else-if="total">
            <slot name="results" :edges="results.edges">
              <b-row class="card-deck mt-2 px-3 px-md-0">
                <b-col cols="12" v-if="lastMatchesPage === 0 && page === 1">
                  <h6 class="text-black"><strong>{{ $t('contacts.result-headings.matches-count', { n: totalMatches }) }}</strong></h6>
                  <p>There are no matches</p>
                  <h6 class="text-black"><strong>{{ $t('contacts.result-headings.contacts-count', { n: totalContacts - totalMatches }) }}</strong></h6>
                </b-col>
                <b-col cols="12" v-else-if="page <= lastMatchesPage">
                  <h6 class="text-black"><strong>{{ $t('contacts.result-headings.matches-count', { n: totalMatches }) }}</strong></h6>
                </b-col>
                <b-col cols="12" v-else-if="page > lastMatchesPage">
                  <h6 class="text-black"><strong>{{ $t('contacts.result-headings.contacts-count', { n: totalContacts - totalMatches }) }}</strong></h6>
                </b-col>

                <template v-for="({ entry, relevance }, i) in results.edges">
                  <b-col cols="12" v-if="cols[i].header">
                    <h6 class="text-black"><strong>{{ $t('contacts.result-headings.contacts-count', { n: totalContacts - totalMatches }) }}</strong></h6>
                  </b-col>

                  <contacts-filter-card class="mb-3" v-if="entry && entry.id" :key="entry.id" :contact="entry" :relevance="relevance">
                    <template v-slot:actions="opts">
                      <slot name="contact-card-actions" v-bind="opts"/>
                    </template>
                  </contacts-filter-card>

                  <b-col v-if="cols[i] && cols[i].pad" cols="12" sm="6" :lg="4 * cols[i].pad"/>
                  <div class="d-none d-sm-block d-lg-none w-100 mt-4" v-if="cols[i].divider"/>
                  <div class="d-none d-lg-block w-100 mt-4" v-if="cols[i].divider"/>
                </template>
              </b-row>
            </slot>
            <div v-if="page && totalPages" class="filter-pagination">
              <p>{{ $t('manage.page-n-of-t', { page, totalPages }) }}</p>
              <b-pagination :value="page" @input="v => $emit('pageUpdated', v)" :per-page="limit" :total-rows="totalPages * limit"/>
            </div>
          </template>
          <slot name="no-results" v-else>
            <p class="my-2 text-center">{{ $tc('manage.total-results', 0) }}</p>
          </slot>
        </b-col>
        <b-col md="4" order="1" order-md="2">
          <div class="d-flex justify-content-end align-items-center">
            <sort v-if="orderOptions" i18n-prefix="contacts.orderBy" :options="orderOptions" :value="order" @input="v => $emit('orderUpdated', v)"/>
            <toggle-filters class="ml-3" v-if="$slots['under-filters']" v-model="showHorizontalWhere"/>
          </div>
          <b-card class="horizontal-where my-2" body-class="p-3" v-if="showHorizontalWhere">
            <slot name="where-options">
              <horizontal-options orientation="vertical" :value="where" @input="v => $emit('whereUpdated', v)"/>
            </slot>
          </b-card>
          <slot name="under-filters"/>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import ContactsFilterCard from '@/components/contacts-filter/card';
import Sort from '@/components/misc/Sort';
import HorizontalOptions from '@/components/contacts-filter/horizontal-options';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import ToggleFilters from '@/components/misc/ToggleFilters';
import grid from '@/mixins/grid';

export default {
  name: 'FilterContactsContainer',
  props: {
    layout: {
      type: String,
      default: () => 'block',
      validate: layout => [ 'block', 'page' ].includes(layout),
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    where: {
      type: Object,
      default: () => ({}),
    },
    order: {
      type: String,
      default: () => null,
    },
    orderOptions: {
      type: Object,
      default: () => null,
    },
    results: {
      type: Object,
      default: () => null,
    },
    showFiltersOnLoad: {
      type: Boolean,
      default: () => true,
    },
  },
  components: {
    ContactsFilterCard,
    Sort,
    LoadingSpinner,
    HorizontalOptions,
    ToggleFilters,
  },
  mixins: [ grid ],
  data() {
    return {
      showHorizontalWhere: this.showFiltersOnLoad,
    };
  },
  computed: {
    page() {
      const { page } = this.results || {};
      return page || 1;
    },
    limit() {
      const { limit } = this.results || {};
      return limit || 10;
    },
    total() {
      const { total } = this.results || {};
      return total || 0;
    },
    totalContacts() {
      const { totalContacts } = this.results || {};
      return totalContacts || 0;
    },
    totalMatches() {
      const { totalMatches } = this.results || {};
      return totalMatches || 0;
    },
    totalPages() {
      const { totalPages } = this.results || {};
      return totalPages || 1;
    },
    lastMatchesPage() {
      const { limit, totalMatches } = this;
      const offset = totalMatches % limit;
      return ((totalMatches - offset) / limit) + (offset > 0 ? 1 : 0);
    },
    lastMatchIndex() {
      if (this.page === this.lastMatchesPage && this.totalMatches === this.limit) {
        return this.limit;
      } else if (this.page === this.lastMatchesPage) {
        const { limit, totalMatches } = this;
        return (totalMatches % limit) - 1;
      } else {
        return null;
      }
    },
    cols() {
      const { results, page, totalMatches, lastMatchesPage, isWindowLG } = this;
      const colCount = isWindowLG ? 3 : 2;

      let cols = results && Array.isArray(results.edges) ? results.edges : [];

      if (totalMatches > 0 && page === lastMatchesPage) {
        cols = cols.map((edge, i) => {
          if (i === (this.lastMatchIndex + 1)) {
            // This is the first non-match, so insert the header
            return { header: true, divider: false };
          } else if (i === this.lastMatchIndex) {
            // This is the last match, so check padded cells
            return { divider: true, pad: (i + 1) % colCount === 0 ? null : colCount - ((i + 1) % colCount) };
          } else if (i > this.lastMatchIndex) {
            // Reset the index to account for newly-placed dividers
            const j = i - this.lastMatchIndex - 1;
            return { divider: (j + 1) % colCount === 0 };
          } else {
            // Just... be normal
            return { divider: (i + 1) % colCount === 0 };
          }
        });
      } else {
        cols = cols.map((edge, i) => ({ divider: (i + 1) % colCount === 0 }));
      }

      if (cols[cols.length - 1].divider !== true) {
        // const lastIndex = cols.length - cols.reverse().findIndex(r => r && r.divider === true) - 1;
        // console.log(cols.reverse().findIndex(r => r && r.divider === true), lastIndex, cols.length);
        cols[cols.length - 1].pad = colCount - cols.slice().reverse().findIndex(({ divider }) => divider === true);
      }

      return cols;
    },
  },
  methods: {
    contactCardListeners(contact) {
      const listeners = {};

      if (this.$listeners.selected) {
        listeners.selected = () => this.$emit('selected', { contact });
      }

      if (this.$listeners.createMatch) {
        listeners.createMatch = type => this.$emit('createMatch', { contact, type });
      }

      if (this.$listeners.unsuitable) {
        listeners.unsuitable = type => this.$emit('unsuitable', { contact, type });
      }

      return listeners;
    },
  },
  // watch: {
  //   results() {
  //     this.showHorizontalWhere = this.showFiltersOnLoad;
  //   },
  // },
};
</script>

<style lang="scss">
.contacts-filter-container {

  .flex-space-between {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .card.horizontal-where {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .summary {
    margin-top:  1.25rem;
    padding: 0 5px 0 5px;
  }

  .is-loading {
    min-height: 42rem;
  }

  .filter-pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

}
</style>



// WEBPACK FOOTER //
// src/components/contacts-filter/container.vue